import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, BarChart3, AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
import api, { isApiError, getErrorMessage } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useBankAccountStore } from '../store/bankAccountStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import {
  searchOperations,
  type SearchResponse,
  type SearchFilters as ApiSearchFilters,
  type RecurrenceMode,
  type RecurrenceFrequency,
  type ValidationMode,
} from '../services/search';
import type { Category } from '../types';
import SearchFilters, {
  EMPTY_FILTERS,
  type SearchFiltersState,
  type SearchFieldErrors,
} from '../components/search/SearchFilters';
import SearchResultsList from '../components/search/SearchResultsList';
import SearchResultsTotals from '../components/search/SearchResultsTotals';
import LockedResultsCard from '../components/search/LockedResultsCard';
import AnalyticsTab from '../components/search/AnalyticsTab';
import type { CategoryNode } from '../components/search/CategoryMultiSelect';

type Tab = 'search' | 'analytics';

const PAGE_SIZE = 50;

function categoryToNode(cat: Category): CategoryNode {
  return {
    id: cat.id,
    name: cat.name,
    isDefault: cat.isDefault,
    subcategories: cat.subcategories.map((s) => ({
      id: s.id,
      name: s.name,
      isDefault: s.isDefault,
    })),
  };
}

function readFiltersFromUrl(params: URLSearchParams): SearchFiltersState {
  const splitCsv = (v: string | null) => (v ? v.split(',').filter(Boolean) : []);
  return {
    dateStart: params.get('dateStart') || '',
    dateEnd: params.get('dateEnd') || '',
    selectedPersonalIds: splitCsv(params.get('personalAccountIds')),
    selectedSharedIds: splitCsv(params.get('sharedAccountIds')),
    categories: {
      expenseCategoryIds: splitCsv(params.get('expenseCategoryIds')),
      expenseSubcategoryIds: splitCsv(params.get('expenseSubcategoryIds')),
      incomeCategoryIds: splitCsv(params.get('incomeCategoryIds')),
      incomeSubcategoryIds: splitCsv(params.get('incomeSubcategoryIds')),
    },
    operationType: (params.get('operationType') as 'all' | 'credit' | 'debit') || 'all',
    amountMin: params.get('amountMin') || '',
    amountMax: params.get('amountMax') || '',
    recurring: (params.get('recurring') as RecurrenceMode) || 'all',
    recurrenceFrequencies: splitCsv(params.get('recurrenceFrequencies')) as RecurrenceFrequency[],
    validated: (params.get('validated') as ValidationMode) || 'all',
    payerPartnerId: (params.get('payerPartnerId') as 'all' | 'partner1' | 'partner2') || 'all',
    searchText: params.get('searchText') || '',
  };
}

function writeFiltersToUrl(filters: SearchFiltersState, page: number): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.dateStart) params.set('dateStart', filters.dateStart);
  if (filters.dateEnd) params.set('dateEnd', filters.dateEnd);
  if (filters.selectedPersonalIds.length) params.set('personalAccountIds', filters.selectedPersonalIds.join(','));
  if (filters.selectedSharedIds.length) params.set('sharedAccountIds', filters.selectedSharedIds.join(','));
  if (filters.categories.expenseCategoryIds.length) params.set('expenseCategoryIds', filters.categories.expenseCategoryIds.join(','));
  if (filters.categories.expenseSubcategoryIds.length) params.set('expenseSubcategoryIds', filters.categories.expenseSubcategoryIds.join(','));
  if (filters.categories.incomeCategoryIds.length) params.set('incomeCategoryIds', filters.categories.incomeCategoryIds.join(','));
  if (filters.categories.incomeSubcategoryIds.length) params.set('incomeSubcategoryIds', filters.categories.incomeSubcategoryIds.join(','));
  if (filters.operationType !== 'all') params.set('operationType', filters.operationType);
  if (filters.amountMin) params.set('amountMin', filters.amountMin);
  if (filters.amountMax) params.set('amountMax', filters.amountMax);
  if (filters.recurring !== 'all') params.set('recurring', filters.recurring);
  if (filters.recurrenceFrequencies.length) params.set('recurrenceFrequencies', filters.recurrenceFrequencies.join(','));
  if (filters.validated !== 'all') params.set('validated', filters.validated);
  if (filters.payerPartnerId !== 'all') params.set('payerPartnerId', filters.payerPartnerId);
  if (filters.searchText) params.set('searchText', filters.searchText);
  if (page > 1) params.set('page', String(page));
  return params;
}

function buildApiFilters(filters: SearchFiltersState, page: number): ApiSearchFilters {
  return {
    dateStart: filters.dateStart || undefined,
    dateEnd: filters.dateEnd || undefined,
    personalAccountIds: filters.selectedPersonalIds.length ? filters.selectedPersonalIds : undefined,
    sharedAccountIds: filters.selectedSharedIds.length ? filters.selectedSharedIds : undefined,
    expenseCategoryIds: filters.categories.expenseCategoryIds.length ? filters.categories.expenseCategoryIds : undefined,
    expenseSubcategoryIds: filters.categories.expenseSubcategoryIds.length ? filters.categories.expenseSubcategoryIds : undefined,
    incomeCategoryIds: filters.categories.incomeCategoryIds.length ? filters.categories.incomeCategoryIds : undefined,
    incomeSubcategoryIds: filters.categories.incomeSubcategoryIds.length ? filters.categories.incomeSubcategoryIds : undefined,
    operationType: filters.operationType === 'all' ? undefined : filters.operationType,
    amountMin: filters.amountMin ? Number(filters.amountMin) : undefined,
    amountMax: filters.amountMax ? Number(filters.amountMax) : undefined,
    recurring: filters.recurring === 'all' ? undefined : filters.recurring,
    recurrenceFrequencies: filters.recurrenceFrequencies.length ? filters.recurrenceFrequencies : undefined,
    validated: filters.validated === 'all' ? undefined : filters.validated,
    payerPartnerId: filters.payerPartnerId === 'all' ? undefined : filters.payerPartnerId,
    searchText: filters.searchText || undefined,
    page,
    pageSize: PAGE_SIZE,
    sortBy: 'date',
    sortOrder: 'desc',
  };
}

export default function SearchPage() {
  const { t } = useTranslation('search');
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const { bankAccounts, sharedBankAccounts, fetchBankAccounts, fetchSharedBankAccounts } = useBankAccountStore();
  const planInfo = useSubscriptionStore((s) => s.planInfo);

  const initialTab = (searchParams.get('tab') as Tab) || 'search';
  const [tab, setTab] = useState<Tab>(initialTab);

  const [filters, setFilters] = useState<SearchFiltersState>(() => readFiltersFromUrl(searchParams));
  // Filtres de la dernière recherche lancée : la pagination les réutilise, et non
  // ceux du formulaire, qui peuvent avoir été modifiés sans relancer la recherche.
  const [appliedFilters, setAppliedFilters] = useState<SearchFiltersState>(() => readFiltersFromUrl(searchParams));

  const [incomeCategories, setIncomeCategories] = useState<CategoryNode[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<CategoryNode[]>([]);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<SearchFieldErrors>({});

  // Charger comptes + catégories au mount
  useEffect(() => {
    fetchBankAccounts();
    if (user?.coupleId) fetchSharedBankAccounts();
  }, [fetchBankAccounts, fetchSharedBankAccounts, user?.coupleId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Sans `context` : la recherche couvre aussi le compte commun, donc ses catégories
        const [incomeRes, expenseRes] = await Promise.all([
          api.get<{ success: boolean; data: Category[] }>('/categories/income'),
          api.get<{ success: boolean; data: Category[] }>('/categories/expense'),
        ]);
        if (cancelled) return;
        setIncomeCategories((incomeRes.data.data || []).map(categoryToNode));
        setExpenseCategories((expenseRes.data.data || []).map(categoryToNode));
      } catch {
        // Ignorer
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const partner1Name = user?.partner1FirstName || t('filters.partner1');
  const partner2Name = user?.partner2FirstName || t('filters.partner2');

  const validateFilters = useCallback(
    (state: SearchFiltersState): SearchFieldErrors => {
      const errs: SearchFieldErrors = {};
      const isoDateRe = /^\d{4}-\d{2}-\d{2}$/;

      if (state.dateStart && !isoDateRe.test(state.dateStart)) {
        errs.dateStart = t('errors.invalidDate');
      }
      if (state.dateEnd && !isoDateRe.test(state.dateEnd)) {
        errs.dateEnd = t('errors.invalidDate');
      }
      if (!errs.dateStart && !errs.dateEnd && state.dateStart && state.dateEnd) {
        if (new Date(state.dateStart) > new Date(state.dateEnd)) {
          errs.dateStart = t('errors.dateStartAfterEnd');
        }
      }

      const minNum = state.amountMin === '' ? null : Number(state.amountMin);
      const maxNum = state.amountMax === '' ? null : Number(state.amountMax);
      if (minNum !== null && (Number.isNaN(minNum) || minNum < 0)) {
        errs.amountMin = Number.isNaN(minNum) ? t('errors.invalidAmount') : t('errors.negativeAmount');
      }
      if (maxNum !== null && (Number.isNaN(maxNum) || maxNum < 0)) {
        errs.amountMax = Number.isNaN(maxNum) ? t('errors.invalidAmount') : t('errors.negativeAmount');
      }
      if (!errs.amountMin && !errs.amountMax && minNum !== null && maxNum !== null && minNum > maxNum) {
        errs.amountMin = t('errors.amountMinAfterMax');
      }
      return errs;
    },
    [t],
  );

  const runSearch = useCallback(
    async (state: SearchFiltersState, targetPage: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await searchOperations(buildApiFilters(state, targetPage));
        setResults(data);
        setFieldErrors({});
      } catch (err) {
        if (isApiError(err) && err.response?.status === 400) {
          const data = err.response.data as {
            message?: string;
            errors?: Array<{ field: string; message: string }>;
          };
          if (Array.isArray(data.errors) && data.errors.length > 0) {
            const apiFieldMap: SearchFieldErrors = {};
            for (const item of data.errors) {
              if (item.field === 'dateStart' || item.field === 'dateEnd' || item.field === 'amountMin' || item.field === 'amountMax') {
                apiFieldMap[item.field] = item.message;
              }
            }
            if (Object.keys(apiFieldMap).length > 0) {
              setFieldErrors(apiFieldMap);
              setError(t('errors.validation'));
              return;
            }
          }
          setError(data.message || t('errors.validation'));
          return;
        }
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    },
    [t],
  );

  const hasUrlFilters = useMemo(() => {
    const keys = Array.from(searchParams.keys()).filter((k) => k !== 'tab');
    return keys.length > 0;
  }, [searchParams]);

  // Au chargement (refresh, lien partagé) et sur précédent/suivant du navigateur,
  // l'URL fait foi : on en relit les filtres et on relance la recherche.
  const location = useLocation();
  const navigationType = useNavigationType();
  const isFirstSync = useRef(true);
  useEffect(() => {
    const isMount = isFirstSync.current;
    isFirstSync.current = false;
    if (!isMount && navigationType !== 'POP') return;

    const urlFilters = readFiltersFromUrl(searchParams);
    const urlPage = parseInt(searchParams.get('page') || '1', 10) || 1;
    const urlTab = (searchParams.get('tab') as Tab) || 'search';
    setTab(urlTab);
    setFilters(urlFilters);
    setAppliedFilters(urlFilters);
    setFieldErrors({});
    if (hasUrlFilters && urlTab === 'search') {
      runSearch(urlFilters, urlPage);
    } else {
      setResults(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleFiltersChange = (next: SearchFiltersState) => {
    setFilters(next);
    if (Object.keys(fieldErrors).length === 0) return;
    const cleared: SearchFieldErrors = { ...fieldErrors };
    if (next.dateStart !== filters.dateStart) {
      delete cleared.dateStart;
      delete cleared.dateEnd;
    }
    if (next.dateEnd !== filters.dateEnd) {
      delete cleared.dateStart;
      delete cleared.dateEnd;
    }
    if (next.amountMin !== filters.amountMin) {
      delete cleared.amountMin;
      delete cleared.amountMax;
    }
    if (next.amountMax !== filters.amountMax) {
      delete cleared.amountMin;
      delete cleared.amountMax;
    }
    setFieldErrors(cleared);
  };

  const handleSubmit = () => {
    const errs = validateFilters(filters);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError(t('errors.validation'));
      return;
    }
    setFieldErrors({});
    const newParams = writeFiltersToUrl(filters, 1);
    if (tab !== 'search') newParams.set('tab', tab);
    setSearchParams(newParams, { replace: false });
    setAppliedFilters(filters);
    runSearch(filters, 1);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setResults(null);
    setError(null);
    setFieldErrors({});
    const newParams = new URLSearchParams();
    if (tab !== 'search') newParams.set('tab', tab);
    setSearchParams(newParams, { replace: false });
  };

  const handlePageChange = (newPage: number) => {
    const newParams = writeFiltersToUrl(appliedFilters, newPage);
    if (tab !== 'search') newParams.set('tab', tab);
    setSearchParams(newParams, { replace: false });
    runSearch(appliedFilters, newPage);
  };

  const handleTabChange = (newTab: Tab) => {
    setTab(newTab);
    const newParams = new URLSearchParams(searchParams);
    if (newTab === 'search') newParams.delete('tab');
    else newParams.set('tab', newTab);
    setSearchParams(newParams, { replace: true });
  };

  const historyMonths = planInfo?.limits.historyMonths;
  const showLocked = results?.locked && historyMonths && historyMonths !== -1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('pageTitle')}</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          <button
            type="button"
            onClick={() => handleTabChange('search')}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
              tab === 'search'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            <SearchIcon className="w-4 h-4" />
            {t('tabs.search')}
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('analytics')}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
              tab === 'analytics'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            <BarChart3 className="w-4 h-4" />
            {t('tabs.analytics')}
          </button>
        </nav>
      </div>

      {tab === 'search' ? (
        <div className="space-y-4">
          <SearchFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onSubmit={handleSubmit}
            onReset={handleReset}
            personalAccounts={bankAccounts}
            sharedAccounts={user?.coupleId ? sharedBankAccounts : []}
            expenseCategories={expenseCategories}
            incomeCategories={incomeCategories}
            partner1Name={partner1Name}
            partner2Name={partner2Name}
            isLoading={isLoading}
            errors={fieldErrors}
          />

          {error && (
            <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          {results && (
            <>
              <SearchResultsTotals totals={results.totals} />
              <SearchResultsList
                operations={results.operations}
                page={results.pagination.page}
                pageSize={results.pagination.pageSize}
                total={results.pagination.total}
                onPageChange={handlePageChange}
                isLoading={isLoading}
              />
              {showLocked && results.locked && (
                <LockedResultsCard
                  locked={results.locked}
                  historyMonths={historyMonths}
                />
              )}
            </>
          )}

          {!results && !isLoading && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
              {t('initial.hint')}
            </div>
          )}
        </div>
      ) : (
        <AnalyticsTab
          filters={filters}
          onFiltersChange={handleFiltersChange}
          fieldErrors={fieldErrors}
          setFieldErrors={setFieldErrors}
          validate={validateFilters}
          hasCoupleAccount={!!user?.coupleId}
          personalAccounts={bankAccounts}
          sharedAccounts={user?.coupleId ? sharedBankAccounts : []}
          expenseCategories={expenseCategories}
          incomeCategories={incomeCategories}
          partner1Name={partner1Name}
          partner2Name={partner2Name}
        />
      )}
    </div>
  );
}
